import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import size from 'lodash/size';
import { injectIntl } from 'react-intl';
import CSSModules from 'react-css-modules';
import cn from 'classnames';

import Icon from '../../Icon';
import ValidationError from '../ValidationError';
import { COLORS } from '../../../constants/styles';
import FilePicker from '../../Chat/components/FilePicker';
import SimpleImagePreview from '../SimpleImagePreview';
import { IMAGE_ALLOWED_FORMATS } from '../../../constants/index';
import generateImagePreview from '../../../utils/generateImagePreview';
import { emitImagePreview } from '../../../utils/events/imagePreview';

import styles from './index.styl';

const ImageUploader = ({
  title,
  label,
  disabled,
  src,
  name,
  onChange,
  intl: { formatMessage },
  maxFileSize,
  errorTextClassName,
}) => {
  const defaultState = {
    previewImage: src,
    fileError: false,
    file: null,
    loading: false,
    deleted: false,
    errors: null,
  };

  const [attachment, setAttachment] = useState(defaultState);

  useEffect(() => {
    setAttachment({ ...attachment, previewImage: src });
  }, [src]);

  async function handleAttachmentPick(file) {
    const previewBlob = await generateImagePreview(file, false, 120);
    const url = window.URL.createObjectURL(previewBlob);

    return {
      file,
      previewImage: url,
    };
  }

  const handleAttachments = file => {
    setAttachment({
      ...attachment,
      loading: true,
      fileError: false,
    });
    handleAttachmentPick(file)
      // eslint-disable-next-line promise/always-return
      .then(result => {
        const data = {
          ...attachment,
          ...result,
          loading: false,
          deleted: false,
          fileError: false,
        };
        setAttachment(data);
        onChange(data);
      })
      .catch(err => {
        const errorData = {
          ...attachment,
          fileError: true,
          errors: null,
          loading: false,
          deleted: false,
        };
        setAttachment(errorData);
        onChange(errorData);
      });
  };

  const removeAttachedFile = () => {
    const removeInfo = { ...defaultState, previewImage: null, deleted: true };
    setAttachment(removeInfo);
    onChange(removeInfo);
  };

  const handleError = err => {
    const errorData = {
      ...attachment,
      errors: get(err, 'errors'),
      fileError: true,
    };
    setAttachment(errorData);
    onChange(errorData);
  };

  const imageSrc = get(attachment, 'previewImage');
  const fileError = get(attachment, 'fileError');
  const errors = get(attachment, 'errors');
  const errorText = size(errors)
    ? formatMessage(
        { id: 'community.newPost.fileError' },
        { size: maxFileSize },
      )
    : formatMessage({ id: 'errors.common' });

  return (
    <div styleName="root">
      <If condition={Boolean(title)}>
        <p styleName="title">{title}</p>
      </If>
      <If condition={Boolean(!imageSrc)}>
        <div
          styleName={cn('placeholder', {
            'placeholder--disabled': disabled,
          })}
        >
          <FilePicker
            maxSize={maxFileSize}
            allowedFormats={IMAGE_ALLOWED_FORMATS}
            inputFileLabel={name}
            onError={handleError}
            onFilePick={handleAttachments}
          >
            <div className="flex flex-column items-center">
              <If condition={!disabled}>
                <Icon
                  name="icon-plus"
                  className="mb2"
                  style={{ fill: COLORS.blueGray }}
                  size={24}
                />
              </If>
              <span styleName="label">{label}</span>
            </div>
          </FilePicker>
        </div>
        <Else />
        <SimpleImagePreview
          handleClick={() => emitImagePreview(imageSrc)}
          key={name}
          src={imageSrc}
          onCancel={removeAttachedFile}
          small
          className="ma0"
          imageClassName="fit-contain bg-white"
          showCloseBtn={!disabled}
        />
      </If>
      <If condition={fileError}>
        <ValidationError content={errorText} className={errorTextClassName} />
      </If>
    </div>
  );
};

ImageUploader.propTypes = {
  title: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  src: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  maxFileSize: PropTypes.number,
  errorTextClassName: PropTypes.string,
};

export default injectIntl(
  CSSModules(ImageUploader, styles, { allowMultiple: true }),
);



// WEBPACK FOOTER //
// ./src/components/common/ImageUploader/index.jsx